//import type { FilterConfig } from './filters/types';

// Типы для конфигов (минимальные, для базовой типизации)
export interface AppConfig {
  api: {
    baseUrl: string;
    timeout: number;
    retryAttempts: number;
  };
  geoserver: {
    wmsUrl: string;
    wfsUrl: string;
    workspace: string;
    layers: {
      traverses: string;
      observations: string;
      sites: string;
    };
  };
  map: {
    defaultCenter: [number, number];
    defaultZoom: number;
    minZoom: number;
    maxZoom: number;
  };
  cache: {
    ttl: number;
    enabled: boolean;
  };
  ui: {
    sidebarWidth: number;
    animationDuration: number;
    dateFormat: string;
  };
}

export interface LayerSourceConfig {
  url?: string;
  attribution?: string;
  layerName?: string;
  geoserverLayer?: string;
  type?: 'xyz' | 'wms' | 'wfs' | 'geoserver_wfs' | 'geojson' | 'api';
  zIndex?: number;
}

export interface LayerConfig {
  id: string;
  name: string;
  visible: boolean;
  hidden?: boolean;
  type: 'xyz' | 'wms' | 'wfs';
  source: LayerSourceConfig;
  style?: any; // Стили могут быть сложными, оставим any
}

export interface LayerGroup {
  id: string;
  name: string;
  exclusive: boolean;
  visible?: boolean;
  hidden?: boolean;
  layers?: LayerConfig[];
  groups?: LayerGroup[];
}

export interface LayersConfig {
  groups: LayerGroup[];
}

export interface LayerStylesConfig {
  [layerId: string]: any;
}

export interface FiltersConfig {
  filters: any[];
}

export interface PagesConfig {
  pages: any[];
}

// Маппинг секций на типы
interface ConfigSections {
  main: AppConfig;
  layers: LayersConfig;
  'layer-styles': LayerStylesConfig;
  filters: FiltersConfig; 
  pages: PagesConfig;
}

type ConfigSection = keyof ConfigSections;

const configCache: Partial<ConfigSections> = {};
let isInitialized = false;

// Инициализация — вызывается один раз при старте приложения
export function initConfig(): void {
  if (isInitialized) return;
  const windowAny = window as any;
  
  // Основной конфиг ОБЯЗАТЕЛЕН
  if (!windowAny.APP_CONFIG) {
    throw new Error('❌ Критическая ошибка: APP_CONFIG не найден в window');
  }
  configCache.main = windowAny.APP_CONFIG as AppConfig;
  
  // Слои — опциональны, но желательны
  if (windowAny.LAYERS_CONFIG) {
    configCache.layers = windowAny.LAYERS_CONFIG as LayersConfig;
  } else {
    console.warn('⚠️ LAYERS_CONFIG не найден');
  }
  
  // Стили слоёв — опциональны
  if (windowAny.LAYER_STYLES) {
    configCache['layer-styles'] = windowAny.LAYER_STYLES as LayerStylesConfig;
  } else {
    console.warn('⚠️ LAYER_STYLES не найден');
  }

  if (windowAny.FILTERS_CONFIG) {
    configCache.filters = windowAny.FILTERS_CONFIG as FiltersConfig;  // <-- Исправлено
  } else {
    console.warn('⚠️ FILTERS_CONFIG не найден');
  }
  
  if (windowAny.PAGES_CONFIG) {
    configCache.pages = windowAny.PAGES_CONFIG as PagesConfig;
  } else {
    console.warn('⚠️ PAGES_CONFIG не найден');
  }
  
  isInitialized = true;
  console.log('✅ Конфигурация инициализирована');
}

export function getConfig<K extends ConfigSection>(section: K): ConfigSections[K] {
  if (!isInitialized) {
    initConfig();
    if (!isInitialized) throw new Error(`Конфигурация не инициализирована. Вызовите initConfig() перед использованием getConfig()`);
  }
  
  const config = configCache[section];
  if (!config) {
    throw new Error(`Конфигурация "${section}" не найдена`);
  }
  
  return config as ConfigSections[K];
}

export function getConfigOptional<K extends ConfigSection>(section: K): ConfigSections[K] | null {
  if (!isInitialized) return null;
  initConfig();
  return (configCache[section] as ConfigSections[K]) || null;
}